import { MAX_ACTIVE_GROUP_MEMBERS_NUMBER } from '@smarty-nx/code-utils'

import {
  selectAcceptedTermsAt,
  selectCanAddGroupMembersByStatus,
  selectGroupSize,
  selectInvitationsCount,
  selectIsGroupOwner,
  selectUserGroupId,
  selectUserGroupVersion,
} from '../../state/dashboard/dashboard.slice'
import { useAppSelector } from '../../state/rootReducer'
import { useIsAuthenticated } from '../use-is-authenticated'

export enum USER_TYPE {
  NEW_USER,
  SOLO_USER,
  GROUP_V1_MEMBER,
  GROUP_V1_OWNER,
  GROUP_V2_MEMBER,
  GROUP_V2_OWNER,
  GROUP_OWNER_V1_FULL_GROUP,
  GROUP_OWNER_V2_FULL_GROUP,
  GROUP_OWNER_V1_INACTIVE,
  GROUP_OWNER_V2_INACTIVE,
}

const getIsFullGroup = (
  groupSize: number,
  groupInvitationsCount: number,
  groupVersion: number
): boolean => {
  if (groupVersion === 1) {
    return groupSize + groupInvitationsCount >= MAX_ACTIVE_GROUP_MEMBERS_NUMBER
  }

  return groupSize >= MAX_ACTIVE_GROUP_MEMBERS_NUMBER
}

export const useGetUserType = (): USER_TYPE => {
  const isAuthenticated = useIsAuthenticated()
  const groupId = useAppSelector(selectUserGroupId)
  const isOwner = useAppSelector(selectIsGroupOwner)
  const groupVersion = useAppSelector(selectUserGroupVersion)
  const acceptedTermsAt = useAppSelector(selectAcceptedTermsAt)
  const groupSize = useAppSelector(selectGroupSize)
  const groupInvitationsCount = useAppSelector(selectInvitationsCount)
  const canAddGroupMembersByStatus = useAppSelector(
    selectCanAddGroupMembersByStatus
  )

  const isGroup = !!groupId
  const isGroupV1 = isGroup && groupVersion === 1
  const isGroupV2 = isGroup && groupVersion === 2
  const isGroupV1Owner = isGroupV1 && isOwner
  const isGroupV2Owner = isGroupV2 && isOwner
  const isGroupFull = getIsFullGroup(
    groupSize || 0,
    groupInvitationsCount || 0,
    groupVersion || 2
  )

  const userTypeMap = [
    {
      condition: !isAuthenticated,
      type: USER_TYPE.NEW_USER,
    },
    {
      condition: isAuthenticated && !isGroup,
      type: USER_TYPE.SOLO_USER,
    },
    {
      condition: isGroupV1Owner && isGroupFull,
      type: USER_TYPE.GROUP_OWNER_V1_FULL_GROUP,
    },
    {
      condition: isGroupV2Owner && isGroupFull,
      type: USER_TYPE.GROUP_OWNER_V2_FULL_GROUP,
    },
    {
      condition: isGroupV1Owner && !canAddGroupMembersByStatus,
      type: USER_TYPE.GROUP_OWNER_V1_INACTIVE,
    },
    {
      condition: isGroupV2Owner && !canAddGroupMembersByStatus,
      type: USER_TYPE.GROUP_OWNER_V2_INACTIVE,
    },
    {
      condition: isGroupV1Owner,
      type: USER_TYPE.GROUP_V1_OWNER,
    },
    {
      condition: isGroupV1 && !isOwner && acceptedTermsAt,
      type: USER_TYPE.GROUP_V1_MEMBER,
    },
    {
      condition: isGroupV2Owner,
      type: USER_TYPE.GROUP_V2_OWNER,
    },
    {
      condition: isGroupV2 && !isOwner && acceptedTermsAt,
      type: USER_TYPE.GROUP_V2_MEMBER,
    },
  ]

  const userTypeItem = userTypeMap.find(({ condition }) => condition)

  return userTypeItem ? userTypeItem.type : USER_TYPE.SOLO_USER
}
